import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Typography, CircularProgress } from '@mui/material';
import CustomDialog from '../../components/dialog';
import { getLeagueDetail, acceptInvitation } from '../../services/leagues';
import toast from 'react-hot-toast';
import { localStorageInstance } from '../../utils/storage';

const INVITATION_STORAGE_KEY = 'league_invitation_data';

const LeagueInvitation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [leagueDetails, setLeagueDetails] = useState(null);
  const [accepting, setAccepting] = useState(false);

  // Get invitation data from URL or localStorage
  const getInvitationData = () => {
    const urlLeagueId = searchParams.get('league_id');
    const urlToken = searchParams.get('token');

    // If URL has parameters, use them
    if (urlLeagueId && urlToken) {
      return { leagueId: urlLeagueId, token: urlToken };
    }

    // Otherwise check localStorage
    const storedData = localStorageInstance.getItem(INVITATION_STORAGE_KEY);
    if (storedData) {
      try {
        return JSON.parse(storedData);
      } catch (error) {
        console.error('Error parsing stored invitation data:', error);
        localStorageInstance.removeItem(INVITATION_STORAGE_KEY);
      }
    }

    return null;
  };

  const fetchLeagueDetails = async (invitationData) => {
    if (!invitationData?.leagueId) return;

    try {
      setLoading(true);
      const response = await getLeagueDetail(invitationData.leagueId);
      setLeagueDetails(response.data);
      setOpen(true);
    } catch (error) {
      if (error?.response?.status === 401) {
        // Store the invitation data and redirect to login
        localStorageInstance.setItem(
          INVITATION_STORAGE_KEY,
          JSON.stringify(invitationData)
        );
        navigate('/login');
      } else {
        toast.error(error?.message || 'Failed to fetch league details');
        localStorageInstance.removeItem(INVITATION_STORAGE_KEY);
        navigate('/my_leagues');
      }
    } finally {
      setLoading(false);
    }
  };

  // Effect to handle invitation data on mount and URL changes
  useEffect(() => {
    let mounted = true;
    const invitationData = getInvitationData();

    if (invitationData && mounted) {
      fetchLeagueDetails(invitationData);
    }

    return () => {
      mounted = false;
    };
  }, []);

  const handleAcceptInvitation = async () => {
    const invitationData = getInvitationData();
    if (!invitationData) {
      toast.error('Invalid invitation data');
      return;
    }

    try {
      setAccepting(true);
      await acceptInvitation(invitationData.leagueId, invitationData.token);
      toast.success('Successfully joined the league!');
      setOpen(false);
      localStorageInstance.removeItem(INVITATION_STORAGE_KEY);
      navigate('/my_leagues', { replace: true });
    } catch (error) {
      if (error?.response?.status === 401) {
        localStorageInstance.setItem(
          INVITATION_STORAGE_KEY,
          JSON.stringify(invitationData)
        );
        navigate('/login');
      } else {
        toast.error(error?.message || 'Failed to accept invitation');
      }
    } finally {
      setAccepting(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    localStorageInstance.removeItem(INVITATION_STORAGE_KEY);
    navigate('/my_leagues', { replace: true });
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <CustomDialog
      open={open}
      handleClose={handleClose}
      handleSubmit={handleAcceptInvitation}
      buttonTxt="Accept"
      title="League Invitation"
      cancelBtn={true}
      alignActions="center"
      fullWidth={true}
      disableSubmit={accepting}
      isLoading={accepting}
    >
      <div className="league-invitation-content">
        <Typography variant="h6" className="mb-3">
          You've been invited to join:
        </Typography>
        {leagueDetails && (
          <>
            <Typography variant="h5" className="mb-2">
              {leagueDetails.league_name}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {leagueDetails.league_description}
            </Typography>
          </>
        )}
      </div>
    </CustomDialog>
  );
};

export default LeagueInvitation; 